import { Portal } from '@mui/material';

import {
  DrawerProvider,
  DynamicGrid,
  DynamicGridActionBar,
  DynamicGridBackdrop,
  DynamicGridProvider,
  IconButton,
  Icons,
  useDynamicGridContext,
} from '@cast/design-system';

import { WORKLOAD_CATEGORIES, type WorkloadCategory } from '../_constants';

import { InventoryWorkloadsCategoryToggle } from '../_components/InventoryWorkloadsCategoryToggle';
import {
  INVENTORY_WORKLOADS_SETTINGS_SLOT_ID,
  InventoryWorkloadsFilters,
} from '../_components/InventoryWorkloadsFilters';
import { InventoryWorkloadsRangePicker } from '../_components/InventoryWorkloadsRangePicker';
import { createInventoryCustomizeDrawer } from '../_components/InventoryDashboardCustomizeDrawer/InventoryDashboardCustomizeDrawer';
import {
  WORKLOAD_DASHBOARD_DEFAULT_VISIBLE,
  WORKLOAD_DASHBOARD_ITEM_CATEGORY,
  WORKLOAD_DASHBOARD_ITEMS,
  WORKLOAD_DRAWER_CATEGORY_OPTIONS,
  WorkloadDashboardItemId,
  getWorkloadDashboardStorageKey,
} from './dashboard/_constants';
import { CpuAutoscalingChart } from './dashboard/autoscaling/CpuAutoscalingChart';
import { CpuPredictionChart } from './dashboard/autoscaling/CpuPredictionChart';
import { MemoryAutoscalingChart } from './dashboard/autoscaling/MemoryAutoscalingChart';
import { WorkloadOOMKillsChart } from './dashboard/autoscaling/WorkloadOOMKillsChart';
import { HpaCharts } from './dashboard/autoscaling/HpaCharts';
import { JvmHeapChart } from './dashboard/jvm/JvmHeapChart';
import { GpuUtilizationChart } from './dashboard/gpu/GpuUtilizationChart';
import { GpuMemoryChart } from './dashboard/gpu/GpuMemoryChart';
import { GpuMemoryWasteChart } from './dashboard/gpu/GpuMemoryWasteChart';
import { GpuCountChart } from './dashboard/gpu/GpuCountChart';
import { GpuWasteChart } from './dashboard/gpu/GpuWasteChart';
import { GpuUtilizationBreakdownChart } from './dashboard/gpu/GpuUtilizationBreakdownChart';
import { ReliabilityLatencyChart } from './dashboard/reliability/ReliabilityLatencyChart';
import { ReliabilityTrafficChart } from './dashboard/reliability/ReliabilityTrafficChart';
import { CpuCostChartWidget } from './dashboard/compute/CpuCostChartWidget';
import { MemoryCostChartWidget } from './dashboard/compute/MemoryCostChartWidget';
import { GpuCostChartWidget } from './dashboard/compute/GpuCostChartWidget';
import { StorageCostChartWidget } from './dashboard/compute/StorageCostChartWidget';
import { ComputeSpendCard } from './dashboard/compute/ComputeSpendCard';
import { NetworkCostChart } from './dashboard/network/NetworkCostChart';
import { NetworkTrafficChart } from './dashboard/network/NetworkTrafficChart';
import { NetworkTrafficBreakdownChart } from './dashboard/network/NetworkTrafficBreakdownChart';
import { CrossAzCommunicationCostsCard } from './dashboard/network/CrossAzCommunicationCostsCard';
import { CrossAzCommunicationTrafficCard } from './dashboard/network/CrossAzCommunicationTrafficCard';

const WorkloadCustomizeDrawer = createInventoryCustomizeDrawer(
  WORKLOAD_DASHBOARD_ITEM_CATEGORY,
  WORKLOAD_DRAWER_CATEGORY_OPTIONS
);

const WorkloadDashboardCustomizeButton = () => {
  const { isCustomizing, setIsCustomizing } = useDynamicGridContext();

  return (
    <IconButton
      variant="secondary"
      size="sm"
      disabled={isCustomizing}
      onClick={() => setIsCustomizing(true)}
      testId="workload-dashboard-customize"
      aria-label="Customize dashboard"
    >
      <Icons.Sliders />
    </IconButton>
  );
};

type InventoryWorkloadDashboardArgs = {
  category?: WorkloadCategory;
};

export const InventoryWorkloadDashboard = ({
  category = WORKLOAD_CATEGORIES.DEFAULT,
}: InventoryWorkloadDashboardArgs) => {
  const storageKey = getWorkloadDashboardStorageKey(category);
  const defaultVisibleItems =
    WORKLOAD_DASHBOARD_DEFAULT_VISIBLE[
      category as Exclude<WorkloadCategory, typeof WORKLOAD_CATEGORIES.SAVINGS>
    ] ?? WORKLOAD_DASHBOARD_DEFAULT_VISIBLE[WORKLOAD_CATEGORIES.DEFAULT];

  return (
    <>
      <InventoryWorkloadsFilters
        categoryToggle={
          <InventoryWorkloadsCategoryToggle
            hiddenCategories={[WORKLOAD_CATEGORIES.SAVINGS]}
          />
        }
        dateControl={<InventoryWorkloadsRangePicker />}
      />
      <DynamicGridProvider
        key={storageKey}
        storageKey={storageKey}
        defaultVisibleItems={defaultVisibleItems}
        items={WORKLOAD_DASHBOARD_ITEMS}
      >
        <DynamicGridBackdrop />
        <DrawerProvider>
          <Portal
            container={() =>
              document.getElementById(INVENTORY_WORKLOADS_SETTINGS_SLOT_ID)
            }
          >
            <WorkloadDashboardCustomizeButton />
          </Portal>
          <DynamicGrid>
            <CpuAutoscalingChart key={WorkloadDashboardItemId.CpuAutoscaling} />
            <MemoryAutoscalingChart
              key={WorkloadDashboardItemId.MemoryAutoscaling}
            />
            <WorkloadOOMKillsChart key={WorkloadDashboardItemId.OomKills} />
            <CpuPredictionChart key={WorkloadDashboardItemId.CpuPrediction} />
            <JvmHeapChart key={WorkloadDashboardItemId.JvmHeap} />
            <HpaCharts key={WorkloadDashboardItemId.HpaCharts} />
            <GpuUtilizationChart key={WorkloadDashboardItemId.GpuUtilization} />
            <GpuMemoryChart key={WorkloadDashboardItemId.GpuMemory} />
            <GpuMemoryWasteChart key={WorkloadDashboardItemId.GpuMemoryWaste} />
            <GpuCountChart key={WorkloadDashboardItemId.GpuCount} />
            <GpuWasteChart key={WorkloadDashboardItemId.GpuWaste} />
            <GpuUtilizationBreakdownChart
              key={WorkloadDashboardItemId.GpuUtilizationBreakdown}
            />
            <CpuCostChartWidget key={WorkloadDashboardItemId.CpuCost} />
            <MemoryCostChartWidget key={WorkloadDashboardItemId.MemoryCost} />
            <GpuCostChartWidget key={WorkloadDashboardItemId.GpuCost} />
            <StorageCostChartWidget key={WorkloadDashboardItemId.StorageCost} />
            <ComputeSpendCard key={WorkloadDashboardItemId.ComputeSpend} />
            <ReliabilityLatencyChart
              key={WorkloadDashboardItemId.ReliabilityLatency}
            />
            <ReliabilityTrafficChart
              key={WorkloadDashboardItemId.ReliabilityTraffic}
            />
            <NetworkCostChart key={WorkloadDashboardItemId.NetworkCost} />
            <NetworkTrafficChart key={WorkloadDashboardItemId.NetworkTraffic} />
            <NetworkTrafficBreakdownChart
              key={WorkloadDashboardItemId.NetworkTrafficBreakdown}
            />
            <CrossAzCommunicationCostsCard
              key={WorkloadDashboardItemId.CrossAzCommunicationCosts}
            />
            <CrossAzCommunicationTrafficCard
              key={WorkloadDashboardItemId.CrossAzCommunicationTraffic}
            />
          </DynamicGrid>
          <DynamicGridActionBar
            drawerTitle="Customize dashboard"
            drawerComponent={WorkloadCustomizeDrawer}
          />
        </DrawerProvider>
      </DynamicGridProvider>
    </>
  );
};
